import { isObject } from './isObject';
import { SGWTConnectError } from './SGWTConnectError';
import {
  ERR_FAILED_TO_FETCH_OPENID_CLAIMS,
  ERR_FAILED_TO_PARSE_OPENID_CLAIMS,
  ERR_FAILED_TO_VALIDATE_OPENID_CLAIMS,
} from './SGWTConnectErrorList';

/**
 * A sub(!)set of OpenID Standard Claims.
 * See https://openid.net/specs/openid-connect-core-1_0.html#StandardClaims
 */
export interface ISGWTConnectOpenIDStandardClaims {
  /**
   * Subject - Identifier for the End-User at the Issuer.
   *
   * See https://openid.net/specs/openid-connect-core-1_0.html#StandardClaims
   */
  sub: string;

  /**
   * End-User's full name in displayable form including all name parts,
   * possibly including titles and suffixes, ordered according to the End-User's
   * locale and preferences.
   *
   * See https://openid.net/specs/openid-connect-core-1_0.html#StandardClaims
   */
  name: string;

  /**
   * End-User's locale, represented as a BCP47 [RFC5646](https://tools.ietf.org/html/rfc5646)
   * language tag. This is typically an ISO 639-1 Alpha-2 language code in lowercase
   * and an ISO 3166-1 Alpha-2 country code in uppercase, separated by a dash.
   * For example, en-US or fr-CA. As a compatibility note, some implementations have used
   * an underscore as the separator rather than a dash, for example, en_US;
   * Relying Parties MAY choose to accept this locale syntax as well.
   *
   * See https://openid.net/specs/openid-connect-core-1_0.html#StandardClaims
   */
  locale: string;

  /**
   * Other Claims MAY be used in conjunction with the standard Claims.
   * See https://openid.net/specs/openid-connect-core-1_0.html#AdditionalClaims
   */
  [key: string]: string | boolean | number | { [key: string]: string };
}

export type ISGWTConnectOpenIDUserInfoCallback = (
  error: SGWTConnectError | null,
  claims?: ISGWTConnectOpenIDStandardClaims,
) => void;

/**
 * Fetches OpenID Claims from the OpenID Provider's "userinfo" endpoint.
 * @param authorizationEndpoint OpenID Provider's "userinfo" endpoint URL.
 * @param authorizationHeader OpenID Provider's "userinfo" endpoint URL.
 * @param callback Called with Claims or error upon request completion.
 */
export function fetchOpenIDUserInfo(
  authorizationEndpoint: string,
  authorizationHeader: string,
  callback: ISGWTConnectOpenIDUserInfoCallback,
) {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', `${authorizationEndpoint}/oauth2/userinfo`);
  xhr.setRequestHeader('Accept', 'application/json');
  xhr.setRequestHeader('Authorization', authorizationHeader);
  xhr.addEventListener('load', function handleOpenIDUserInfoLoad() {
    if (xhr.status === 200) {
      let openIDUserInfo;
      try {
        openIDUserInfo = JSON.parse(xhr.responseText);
      } catch {
        callback(new SGWTConnectError(ERR_FAILED_TO_PARSE_OPENID_CLAIMS));
        return;
      }
      if (isValidOpenIDClaims(openIDUserInfo)) {
        callback(null, openIDUserInfo);
      } else {
        callback(new SGWTConnectError(ERR_FAILED_TO_VALIDATE_OPENID_CLAIMS));
      }
    } else {
      callback(new SGWTConnectError(ERR_FAILED_TO_FETCH_OPENID_CLAIMS));
    }
  });
  xhr.send();
}

/**
 * Determines whether the given OpenID Configuration is valid.
 * @param openIDUserInfo JSON-parsed value obtained from the Discovery API.
 */
function isValidOpenIDClaims(openIDUserInfo: any): openIDUserInfo is ISGWTConnectOpenIDStandardClaims {
  return (
    isObject(openIDUserInfo) &&
    typeof openIDUserInfo.sub === 'string' &&
    typeof openIDUserInfo.name === 'string' &&
    typeof openIDUserInfo.locale === 'string'
  );
}
